import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Row, Col, Spinner, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";

import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import AsplHeroSection from "./AsplHeroSection";

import SearchBar from "./SearchBar";
import ProductCard from "./ProductCard";
import SkeletonCard from "./SkeletonCard";

import { fetchFeatured, searchLive, searchLocalIndex } from "./api";

const AsplHomePage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const urlQuery = new URLSearchParams(search).get("search") || "";

  const [query, setQuery] = useState(urlQuery);
  const [searchResults, setSearchResults] = useState([]);

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const hasLoadedOnce = useRef(false);

  /* --- FIXED: sync query with URL --- */
  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  /* --- Run search when URL changes --- */
  useEffect(() => {
    if (urlQuery && urlQuery.length >= 2) {
      performSearch(urlQuery);
    } else {
      setSearchResults([]);
    }
  }, [urlQuery]);

  /* --- Backend search --- */
  const performSearch = async (term) => {
    setSearchLoading(true);

    try {
      const res = await searchLive(term);

      const productInfos =
        res.data.details?.getProductsInfoResponse?.productInfoList?.productInfo || [];

      const mapped = productInfos.map((item) => ({
        id: item.id,
        name: item.name,
        brand: item.producer,
        category: item.groupName,
        stock: item.stock || 0,
        priceUSD: item.price_usd ?? null,
        priceNGN: item.price_ngn ?? null,
        exchangeRate: item.exchange_rate ?? null,
        firstPhoto: item.photo || null,
      }));

      setSearchResults(mapped);
    } catch (err) {
      console.error("Live search failed, falling back to local index:", err);

      try {
        const fallbackRes = await searchLocalIndex(term, 1);
        const fallbackMapped = (fallbackRes.data.results || []).map((item) => ({
          id: item.aspl_id,
          name: item.name,
          brand: item.brand,
          category: item.category,
          stock: item.stock || 0,
          priceUSD: item.price,
          priceNGN: item.price_ngn,
          exchangeRate: item.exchange_rate,
          firstPhoto: item.photo_url || null,
        }));

        setSearchResults(fallbackMapped);
      } catch (fallbackErr) {
        console.error("Fallback index search failed:", fallbackErr);
        setSearchResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  /* --- Featured products pagination --- */
  const loadFeatured = useCallback(
    async (reset = false) => {
      if (loading) return;
      if (!reset && page > pages) return;

      setLoading(true);

      try {
        const targetPage = reset ? 1 : page;
        const res = await fetchFeatured(targetPage);

        const mapped = (res.data.results || []).map((x) => ({
          id: x.aspl_id,
          name: x.name,
          brand: x.brand,
          category: x.category,
          stock: x.stock,
          priceUSD: x.price,
          priceNGN: x.price_ngn,
          exchangeRate: x.exchange_rate,
          firstPhoto: x.photo_url || "",
        }));

        if (reset) {
          setProducts(mapped);
          setPage(2);
          setPages(res.data.pages || 1);
        } else {
          setProducts((prev) => [...prev, ...mapped]);
          setPage((prev) => prev + 1);
        }
      } catch (err) {
        console.error("Featured load error:", err);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [page, pages, loading]
  );

  /* --- Initial load --- */
  useEffect(() => {
    if (hasLoadedOnce.current) return;
    hasLoadedOnce.current = true;

    if (!urlQuery) loadFeatured(true);
  }, [loadFeatured]);

  /* --- Search from Hero/SearchBar --- */
  const executeSearch = (value) => {
    const q = value || query;
    if (!q.trim()) return;

    navigate(`/aspl?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
      <Navbar />
      <AsplHeroSection executeSearch={executeSearch} />

      <Container className="py-4">
        <SearchBar query={query} setQuery={setQuery} executeSearch={executeSearch} />

        {/* SEARCH MODE */}
        {query && searchLoading && (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        )}

        {query && !searchLoading && (
          <>
            <h4 className="fw-bold text-primary mb-3">
              Search results for "{query}"
            </h4>

            {searchResults.length === 0 && (
              <p className="text-muted">No products found.</p>
            )}

            <Row className="g-3">
              {searchResults.map((p) => (
                <Col key={p.id} xs={6} sm={6} md={4} lg={3}>
                  <ProductCard
                    item={p}
                    onView={() => navigate(`/aspl/product/${p.id}`)}
                  />
                </Col>
              ))}
            </Row>

            <Footer />
          </>
        )}

        {/* FEATURED IF NOT SEARCHING */}
        {!query && (
          <>
            <div
              className="rounded-4 p-3 mb-3"
              style={{
                background: "rgba(13,110,253,.06)",
                border: "1px solid rgba(13,110,253,.15)"
              }}
            >
              <h4 className="mb-0 fw-bold text-primary">Featured ASPL Auto Parts</h4>
            </div>

            {initialLoading ? (
              <Row className="g-3">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Col key={i} xs={6} sm={6} md={4} lg={3}>
                    <SkeletonCard />
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                <Row className="g-3">
                  {products.map((p) => (
                    <Col key={p.id} xs={6} sm={6} md={4} lg={3}>
                      <ProductCard
                        item={p}
                        onView={() => navigate(`/aspl/product/${p.id}`)}
                      />
                    </Col>
                  ))}
                </Row>

                {!loading && page <= pages && (
                  <div className="text-center mt-4">
                    <Button onClick={() => loadFeatured(false)}>Load More</Button>
                  </div>
                )}

                {loading && (
                  <div className="text-center mt-3">
                    <Spinner animation="border" />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Container>

      {!query && <Footer />}
    </>
  );
};

export default AsplHomePage;
